import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { CheckCircle2Icon, EllipsisIcon, MailIcon, UserPlusIcon, UserXIcon, XIcon } from 'lucide-react';
type TeamMemberStatus = 'Active' | 'Invited' | 'Deactivated';
type TeamMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'Owner' | 'Team member';
  status: TeamMemberStatus;
};
type InviteForm = {
  firstName: string;
  lastName: string;
  email: string;
};
const seededMembers: TeamMember[] = [{
  id: 'team_ian',
  firstName: 'Ian',
  lastName: 'Carter',
  email: 'ian@creditmanager.co.uk',
  role: 'Owner',
  status: 'Active'
}, {
  id: 'team_rachel',
  firstName: 'Rachel',
  lastName: 'Green',
  email: 'rachel@creditmanager.co.uk',
  role: 'Team member',
  status: 'Active'
}, {
  id: 'team_tom',
  firstName: 'Tom',
  lastName: 'Hughes',
  email: 'tom@creditmanager.co.uk',
  role: 'Team member',
  status: 'Invited'
}];
const emptyInvite: InviteForm = {
  firstName: '',
  lastName: '',
  email: ''
};
export function TeamPanel() {
  const [members, setMembers] = useState<TeamMember[]>(seededMembers);
  const [invite, setInvite] = useState<InviteForm>(emptyInvite);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isInviteModalOpen) {
      return;
    }
    firstNameRef.current?.focus();
    function handleDialogKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeInviteModal();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
      if (!focusableElements?.length) {
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
    document.addEventListener('keydown', handleDialogKeyDown);
    return () => document.removeEventListener('keydown', handleDialogKeyDown);
  }, [isInviteModalOpen]);
  function openInviteModal() {
    setInvite(emptyInvite);
    setError('');
    setIsInviteModalOpen(true);
  }
  function closeInviteModal() {
    setInvite(emptyInvite);
    setError('');
    setIsInviteModalOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }
  function updateInvite(field: keyof InviteForm, value: string) {
    setInvite(current => ({
      ...current,
      [field]: value
    }));
    setError('');
  }
  function sendInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const firstName = invite.firstName.trim();
    const lastName = invite.lastName.trim();
    const email = invite.email.trim().toLowerCase();
    if (!firstName || !lastName || !email) {
      setError('Enter a first name, last name, and email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (members.some(member => member.email === email)) {
      setError('This email address already belongs to a team member.');
      return;
    }
    setMembers(current => [...current, {
      id: `team_${Date.now()}`,
      firstName,
      lastName,
      email,
      role: 'Team member',
      status: 'Invited'
    }]);
    setSuccessMessage(`Invite sent to ${firstName} ${lastName}.`);
    closeInviteModal();
  }
  function deactivateMember(id: string) {
    setMembers(current => current.map(member => member.id === id ? {
      ...member,
      status: 'Deactivated'
    } : member));
  }
  return <section aria-labelledby="team-title" data-id="element-318" data-mp="3ruym1itw9zap">
      <div className="flex flex-col gap-5 border-b border-[#A6B6F8]/50 pb-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-319" data-mp="3rw1dpsz1k5ro">
        <div data-id="element-320" data-mp="ox6awyzihiq">
          <h1 id="team-title" className="text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-321" data-mp="mkn6ghhq143">
            Team
          </h1>
          <p className="mt-2 text-sm text-stone-600" data-id="element-322" data-mp="9i0ofikkrijp">
            Invite teammates and manage access to Credit Manager.
          </p>
        </div>

        <button ref={triggerRef} type="button" onClick={openInviteModal} className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-[#041F60] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#031849] focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 sm:w-auto" data-id="element-323" data-mp="392nw0pkc9vu0">
          <UserPlusIcon size={17} aria-hidden="true" data-id="element-324" data-mp="2wk1sdqimtnsc" />
          Add new team member
        </button>
      </div>

      {successMessage && <div role="status" className="mt-5 flex items-start gap-3 rounded-xl border border-[#A6B6F8]/60 bg-[#A6B6F8]/15 px-4 py-3 text-sm text-[#5B5299]" data-id="element-325" data-mp="2mnf9v8ds38th">
          <CheckCircle2Icon size={19} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-326" data-mp="2cxvnvturqyue" />
          <div data-id="element-327" data-mp="28x3ff2tq0xz6">
            <p className="font-bold" data-id="element-328" data-mp="28xlgqdv8q2dy">Invitation sent</p>
            <p className="mt-0.5" data-id="element-329" data-mp="23wxue5hyi9q2">{successMessage}</p>
          </div>
        </div>}

      <section className="mt-5 rounded-xl border border-[#A6B6F8]/50 bg-white" aria-labelledby="team-members-title" data-id="element-330" data-mp="1yy4e65bac4m7">
        <div className="border-b border-[#A6B6F8]/50 px-5 py-4" data-id="element-331" data-mp="1p9gy4pidfsrt">
          <h2 id="team-members-title" className="text-base font-bold" data-id="element-332" data-mp="1u9bqyylpisfi">
            Team members
          </h2>
          <p className="mt-0.5 text-xs text-stone-500" data-id="element-333" data-mp="1fcue22xvd5qu">
            {members.filter(member => member.status === 'Active').length}{' '}
            active members
          </p>
        </div>

        <div className="divide-y divide-stone-100" data-id="element-334" data-mp="2819umtwnfw4a">
          {members.map(member => {
          const canDeactivate = member.role !== 'Owner' && member.status !== 'Deactivated';
          return <article key={member.id} className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-335" data-mp="1ya5e3prbs37c">
                <div className="flex min-w-0 items-center gap-3" data-id="element-336" data-mp="2r0453qp2yaen">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-bold ${member.status === 'Deactivated' ? 'bg-stone-100 text-stone-500' : 'bg-[#041F60] text-[#4BE4FF]'}`} aria-hidden="true" data-id="element-337" data-mp="2qxrhawwn55to">
                    {initialsFor(member)}
                  </span>
                  <div className="min-w-0" data-id="element-338" data-mp="u0dfeoor8nn9">
                    <h3 className="truncate text-sm font-bold text-[#041F60]" data-id="element-339" data-mp="u37yabs4e0ek">
                      {member.firstName} {member.lastName}
                    </h3>
                    <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-stone-500" data-id="element-340" data-mp="1dix9yfeu0cy">
                      <MailIcon size={13} aria-hidden="true" data-id="element-341" data-mp="3skc5vncj91ri" />
                      {member.email}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-3" data-id="element-342" data-mp="6baxdfq7kss8">
                  <TeamMemberStatus status={member.status} data-id="element-343" data-mp="6e6xnapj3tju" />
                  <TeamMemberActions member={member} canDeactivate={canDeactivate} onDeactivate={deactivateMember} data-id="element-344" data-mp="1lr9pxinceg7d" />
                </div>
              </article>;
        })}
        </div>
      </section>

      {isInviteModalOpen && <div className="fixed inset-0 z-50 flex items-end bg-[#041F60]/50 p-4 sm:items-center sm:justify-center" onMouseDown={event => {
      if (event.target === event.currentTarget) {
        closeInviteModal();
      }
    }} data-id="element-345" data-mp="3g6oydjxzchu3">
          <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="invite-modal-title" aria-describedby="invite-modal-description" className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl" data-id="element-346" data-mp="3tzoard2gtvxf">
            <div className="flex items-start justify-between gap-4 border-b border-[#A6B6F8]/50 px-5 py-4 sm:px-6" data-id="element-347" data-mp="29kcms10e9t43">
              <div className="flex items-start gap-3" data-id="element-348" data-mp="2ej64y6qmm8al">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#4BE4FF] text-[#041F60]" data-id="element-349" data-mp="2ejpjrekzcgke">
                  <UserPlusIcon size={19} aria-hidden="true" data-id="element-350" data-mp="vq5yxh00adtj" />
                </span>
                <div data-id="element-351" data-mp="qvi655xyyx95">
                  <h2 id="invite-modal-title" className="text-lg font-bold text-[#041F60]" data-id="element-352" data-mp="mw10abemry3g">
                    Add new team member
                  </h2>
                  <p id="invite-modal-description" className="mt-1 text-sm text-stone-600" data-id="element-353" data-mp="1enp94ny7a2hj">
                    Send an invitation to join this Credit Manager workspace.
                  </p>
                </div>
              </div>
              <button type="button" onClick={closeInviteModal} aria-label="Close invite team member dialog" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-stone-500 transition hover:bg-stone-100 hover:text-[#041F60] focus:outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-354" data-mp="10no7v7e0wt1j">
                <XIcon size={19} aria-hidden="true" data-id="element-355" data-mp="hymuh83482fv" />
              </button>
            </div>

            <form className="space-y-4 px-5 py-5 sm:px-6" onSubmit={sendInvite} noValidate data-id="element-356" data-mp="hws8dqg3y4a7">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-357" data-mp="rvrhtb8p63y9">
                <InviteField ref={firstNameRef} id="team-first-name" label="First name" value={invite.firstName} onChange={value => updateInvite('firstName', value)} data-id="element-358" data-mp="15o7c9nnj8tnk" />
                <InviteField id="team-last-name" label="Last name" value={invite.lastName} onChange={value => updateInvite('lastName', value)} data-id="element-359" data-mp="1fkaqhrvnsiqv" />
              </div>

              <InviteField id="team-email" label="Email address" type="email" value={invite.email} onChange={value => updateInvite('email', value)} data-id="element-360" data-mp="3cff5c8hw5d0z" />

              {error && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700" data-id="element-361" data-mp="1i72znsjoycmh">
                  {error}
                </p>}

              <div className="flex flex-col-reverse gap-3 border-t border-[#A6B6F8]/50 pt-5 sm:flex-row sm:justify-end" data-id="element-362" data-mp="194k29nyocs7m">
                <button type="button" onClick={closeInviteModal} className="inline-flex items-center justify-center rounded-lg border border-[#A6B6F8]/60 bg-white px-4 py-2.5 text-sm font-bold text-[#041F60] transition hover:bg-[#A6B6F8]/15 focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2" data-id="element-363" data-mp="1d5m48cjz7ida">
                  Cancel
                </button>
                <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#041F60] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#031849] focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2" data-id="element-364" data-mp="2x4ffmhtpkdxe">
                  <UserPlusIcon size={16} aria-hidden="true" data-id="element-365" data-mp="2i7onmtmd64ht" />
                  Send invite
                </button>
              </div>
            </form>
          </section>
        </div>}
    </section>;
}
type InviteFieldProps = {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
};
const InviteField = forwardRef<HTMLInputElement, InviteFieldProps>(function InviteField({
  id,
  label,
  type = 'text',
  value,
  onChange
}, ref) {
  return <div data-id="element-366" data-mp="2givwkbzq0j2">
        <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-[#041F60]" data-id="element-367" data-mp="2g93kq3h3ha7">
          {label}
        </label>
        <input ref={ref} id={id} type={type} value={value} onChange={event => onChange(event.target.value)} className="block w-full rounded-lg border border-[#A6B6F8]/60 bg-white px-3 py-2.5 text-sm text-[#041F60] outline-none transition focus:border-[#041F60] focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-368" data-mp="5xojav07bc8l" />
      </div>;
});
type TeamMemberActionsProps = {
  member: TeamMember;
  canDeactivate: boolean;
  onDeactivate: (id: string) => void;
};
function TeamMemberActions({
  member,
  canDeactivate,
  onDeactivate
}: TeamMemberActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = `team-member-actions-${member.id}`;
  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);
  function runDeactivate() {
    onDeactivate(member.id);
    setIsOpen(false);
  }
  return <div className="relative" ref={menuRef} data-id="element-369" data-mp="1qewbn7rntjo1">
      <button type="button" onClick={() => setIsOpen(current => !current)} disabled={!canDeactivate} aria-expanded={isOpen} aria-haspopup={canDeactivate ? 'menu' : undefined} aria-controls={canDeactivate ? menuId : undefined} aria-label={canDeactivate ? `Quick actions for ${member.firstName} ${member.lastName}` : `Quick actions unavailable for ${member.firstName} ${member.lastName}`} className={`inline-flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 outline-none transition focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 ${canDeactivate ? isOpen ? 'border-[#041F60] bg-[#041F60] text-white' : 'border-[#A6B6F8]/60 bg-white text-[#5B5299] hover:border-[#5B5299] hover:bg-[#A6B6F8]/15 hover:text-[#041F60]' : 'cursor-not-allowed border-[#A6B6F8]/50 bg-white text-stone-300'}`} data-id="element-370" data-mp="1lfjenu72qw7q">
        <EllipsisIcon size={19} aria-hidden="true" data-id="element-371" data-mp="wz7uko0bb3i" />
        <span className="sr-only" data-id="element-372" data-mp="zlcabflpahi">Quick actions</span>
      </button>

      {isOpen && canDeactivate && <div id={menuId} role="menu" aria-label={`Quick actions for ${member.firstName} ${member.lastName}`} className="absolute right-0 z-20 mt-2 w-52 rounded-xl border border-[#A6B6F8]/60 bg-white p-1.5 shadow-lg" data-id="element-373" data-mp="3nctmw8b4rhwv">
          <button type="button" role="menuitem" onClick={runDeactivate} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none transition hover:bg-rose-50 focus:ring-2 focus:ring-rose-200" data-id="element-374" data-mp="jujsffjqnnib">
            <UserXIcon size={16} className="shrink-0 text-rose-700" aria-hidden="true" data-id="element-375" data-mp="tl0h2g6tstnc" />
            <span className="text-sm font-bold text-rose-700" data-id="element-376" data-mp="pjyepqhm41h5">
              Deactivate member
            </span>
          </button>
        </div>}
    </div>;
}
function TeamMemberStatus({
  status
}: {
  status: TeamMemberStatus;
}) {
  const styleByStatus: Record<TeamMemberStatus, string> = {
    Active: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
    Invited: 'bg-blue-50 text-blue-700 ring-blue-200',
    Deactivated: 'bg-stone-100 text-stone-600 ring-stone-200'
  };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${styleByStatus[status]}`} data-id="element-377" data-mp="1qq1254a2brk">
      {status}
    </span>;
}
function initialsFor(member: TeamMember) {
  return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
}